import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const DEPOSIT_MAIN_PRODUCT_PROPERTY = '__mainProductId';
const DEPOSIT_MAIN_VARIANT_PROPERTY = '__mainVariantId';
const DEPOSIT_VARIANT_PROPERTY = '__depositVariantId';
const DEPOSIT_QUANTITY_PROPERTY = '__depositqty';

const isDepositLineItem = (item) => {
  return item?.properties?.[DEPOSIT_MAIN_PRODUCT_PROPERTY] != null && item.properties[DEPOSIT_MAIN_PRODUCT_PROPERTY] !== '';
};

const getVisibleCartItemCount = (items = []) => {
  return items.reduce((sum, item) => sum + (isDepositLineItem(item) ? 0 : item.quantity || 0), 0);
};

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener('apps:product-added-to-cart', this.handleAppDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener('apps:product-added-to-cart', this.handleAppDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#findCartItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#findCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
        this.#syncDrawerShell();
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const lineItemRow = this.#findCartItemRow(line);
    const hasDeposit = lineItemRow?.dataset.hasDeposit === 'true';

    const request = hasDeposit
      ? this.#updateDepositLinkedItems(line, quantity)
      : this.#performCartMutation(Theme.routes.cart_change_url, {
          line: line,
          quantity: quantity,
          ...this.#getSectionsPayload(),
        });

    request
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles cart refreshes triggered by third-party cart/discount apps.
   */
  handleAppDiscountUpdate = () => {
    if (this.isDrawer) {
      this.#refreshDrawerMarkup();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode: 'full' });
    }
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`input[data-cart-line="${line}"]`)
    );

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (this.isDrawer) {
        this.#refreshDrawerMarkup();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false, mode: 'full' });
      }
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      if (this.isDrawer) {
        this.#replaceDrawerMarkup(cartItemsHtml);
      } else {
        morphSection(this.sectionId, cartItemsHtml, { mode: 'full' });
      }

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      if (this.isDrawer) {
        this.#refreshDrawerMarkup();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false, mode: 'full' });
      }
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #findCartItemRow(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.line === String(line));
  }

  #getSectionsPayload() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return {
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname + window.location.search,
    };
  }

  async #performCartMutation(url, payload, line) {
    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
    const responseText = await response.text();
    const parsedResponseText = JSON.parse(responseText);

    resetShimmer(this);

    if (parsedResponseText.errors) {
      this.#handleCartError(line, parsedResponseText);
      return;
    }

    parsedResponseText.item_count = getVisibleCartItemCount(parsedResponseText.items || []);

    const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    if (this.isDrawer) {
      this.#replaceDrawerMarkup(parsedResponseText.sections[this.sectionId]);
    } else {
      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: 'full' });
    }

    this.#updateCartQuantitySelectorButtonStates();
  }

  async #updateDepositLinkedItems(line, quantity) {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const targetItem = cart.items?.[line - 1];

    if (!targetItem?.key) {
      return this.#performCartMutation(
        Theme.routes.cart_change_url,
        {
          line: line,
          quantity: quantity,
          ...this.#getSectionsPayload(),
        },
        line
      );
    }

    const productId = String(targetItem.product_id);
    const mainVariantId = String(targetItem.variant_id || targetItem.id || '');
    const depositVariantId = String(targetItem.properties?.[DEPOSIT_VARIANT_PROPERTY] || '');
    const customerCountry = (window.Shopify && window.Shopify.country) || '';

    const relatedMainItems = cart.items.filter((item) => {
      return (
        String(item.product_id) === productId &&
        String(item.variant_id || item.id || '') === mainVariantId &&
        String(item.properties?.[DEPOSIT_VARIANT_PROPERTY] || '') === depositVariantId
      );
    });

    const totalDepositQuantity = customerCountry === 'DE'
      ? relatedMainItems.reduce((sum, item) => {
          const nextQuantity = item.key === targetItem.key ? quantity : item.quantity;
          const depositQuantity = Number(item.properties?.[DEPOSIT_QUANTITY_PROPERTY] || 0);
          return sum + nextQuantity * depositQuantity;
        }, 0)
      : 0;

    const linkedDepositLines = cart.items.filter((item) => {
      if (!isDepositLineItem(item)) return false;
      if (String(item.properties?.[DEPOSIT_MAIN_PRODUCT_PROPERTY]) !== productId) return false;
      const linkedMainVariantId = String(item.properties?.[DEPOSIT_MAIN_VARIANT_PROPERTY] || '');
      if (linkedMainVariantId !== '' && linkedMainVariantId !== mainVariantId) return false;
      return depositVariantId === '' || String(item.id) === depositVariantId;
    });

    /** @type {Record<string, number>} */
    const updates = {
      [targetItem.key]: quantity,
    };

    if (linkedDepositLines[0]?.key) {
      updates[linkedDepositLines[0].key] = totalDepositQuantity;
      linkedDepositLines.slice(1).forEach((item) => {
        updates[item.key] = 0;
      });
    }

    return this.#performCartMutation(
      Theme.routes.cart_update_url,
      {
        updates,
        ...this.#getSectionsPayload(),
      },
      line
    );
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Replaces only the drawer cart subtree from server-rendered section HTML.
   * This avoids morphing the full header section while the dialog is open.
   * @param {string} sectionHtml
   */
  #replaceDrawerMarkup(sectionHtml) {
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    this.#syncSectionStylesheet(parsed);

    const nextComponent = Array.from(parsed.querySelectorAll('cart-items-component')).find((component) => {
      return component instanceof HTMLElement && component.dataset.sectionId === this.sectionId;
    });

    if (!(nextComponent instanceof HTMLElement)) {
      this.#refreshDrawerMarkup();
      return;
    }

    this.replaceWith(nextComponent);
    this.#syncDrawerShell(nextComponent);
  }

  /**
   * Refetches the current section and replaces the drawer cart subtree.
   */
  #refreshDrawerMarkup() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId);

    fetch(url.toString())
      .then((response) => response.text())
      .then((html) => this.#replaceDrawerMarkup(html))
      .catch((error) => console.error(error));
  }

  /**
   * Updates the live section stylesheet from a parsed section response.
   * This is needed when the drawer transitions from empty to non-empty markup,
   * because snippet-scoped styles are compiled into the section stylesheet.
   * @param {Document} parsed
   */
  #syncSectionStylesheet(parsed) {
    const liveSection = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!(liveSection instanceof HTMLElement)) return;

    const nextStylesheet = parsed.querySelector('style[data-section-stylesheet]');
    if (!(nextStylesheet instanceof HTMLStyleElement)) return;

    const liveStylesheet = liveSection.querySelector('style[data-section-stylesheet]');
    if (liveStylesheet instanceof HTMLStyleElement) {
      liveStylesheet.textContent = nextStylesheet.textContent;
      return;
    }

    liveSection.prepend(nextStylesheet.cloneNode(true));
  }

  /**
   * Keeps the drawer shell classes and accessibility labels aligned with the rendered cart contents.
   * @param {Element | ParentNode} [root]
   */
  #syncDrawerShell(root = this) {
    if (!this.isDrawer && root === this) return;

    const dialogHost = root instanceof Element ? root.closest('cart-drawer-component') : this.closest('cart-drawer-component');
    const dialog = dialogHost?.querySelector('.cart-drawer__dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const isEmpty = !root.querySelector('.cart-form');
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');

    if (isEmpty) {
      dialog.setAttribute('cart-summary-sticky', 'false');
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
